import type { WorkspaceDoc, WorkspaceCollection } from '@/lib/sync/entity-types';

/**
 * A single recent-entity row, normalized to the minimal shape the sidebar
 * Recents section needs to render (icon via `kind`) and open (via `id`).
 */
export interface RecentEntity {
  id: string;
  kind: 'document' | 'canvas' | 'collection' | 'conversation';
  title: string;
}

/**
 * Minimal conversation shape consumed by {@link selectRecentEntities}.
 * Structurally satisfied by the entity store's `ConversationRow` — we accept
 * only the fields we need so the helper stays decoupled and trivially testable.
 * Conversations track recency via `latestActivityAt` rather than `updatedAt`.
 */
export interface RecentConversation {
  id: string;
  title: string;
  isArchived: boolean;
  latestActivityAt: string;
}

/**
 * Selects the most recently-active workspace entities for the sidebar
 * "Recents" section.
 *
 * Merges documents (kind document/canvas), collections, and
 * conversations into a single list, drops archived entities, sorts by recency
 * descending, and caps at `limit`. Recency is `updatedAt` for docs/collections
 * and `latestActivityAt` for conversations — matching the "recent" pattern used
 * on the home page and in the assistant history (no true visit log).
 */
export function selectRecentEntities(
  docs: readonly WorkspaceDoc[],
  collections: readonly WorkspaceCollection[],
  conversations: readonly RecentConversation[],
  limit: number,
): RecentEntity[] {
  type Dated = RecentEntity & { recencyAt: string };

  const docEntries: Dated[] = docs
    .filter((d) => !d.isArchived)
    .map((d) => ({ id: d.id, kind: d.kind, title: d.title, recencyAt: d.updatedAt }));

  const collectionEntries: Dated[] = collections
    .filter((c) => !c.isArchived)
    .map((c) => ({ id: c.id, kind: 'collection' as const, title: c.name, recencyAt: c.updatedAt }));

  const conversationEntries: Dated[] = conversations
    .filter((c) => !c.isArchived)
    .map((c) => ({ id: c.id, kind: 'conversation' as const, title: c.title, recencyAt: c.latestActivityAt }));

  return [...docEntries, ...collectionEntries, ...conversationEntries]
    .sort((a, b) => new Date(b.recencyAt).getTime() - new Date(a.recencyAt).getTime())
    .slice(0, limit)
    .map(({ id, kind, title }) => ({ id, kind, title }));
}
